import * as THREE from 'three';
import { Scene } from './Scene';
import { theme, createStarfield } from '../visual';
import type { EngineEffect } from '../visual';
import type { InputManager } from '../input';
import {
  PlayerShip,
  ShipControlSystem,
  TrailUpdateSystem
} from '../gameplay/ships';
import { buildSector, PLAYER_SPAWN } from '../gameplay/sector';
import type { Landable, SectorBuildResult, Star } from '../gameplay/sector';
import { CollisionSystem } from '../gameplay/physics';
import { CombatSystem, Explosions } from '../gameplay/combat';
import { gameConfig } from '../config';
import type { AudioManager } from '../audio';
import type { FactionSelection } from '../gameplay/factions';
import type { PlayerStats } from '../gameplay/player';
import {
  Asteroid,
  AsteroidLodSystem,
  AsteroidRespawner,
  PlanetaryOrbitCapture,
  Station,
  Moon,
  Planet
} from '../gameplay/sector';
import {
  NpcDirector,
  HostileProjectiles,
  NpcShip
} from '../gameplay/npcs';
import type { Inventory } from '../gameplay/inventory';
import type { ShipUpgrades } from '../gameplay/upgrades';
import { ShipMiningBeam } from '../gameplay/mining';
import { rollAsteroidLoot } from '../gameplay/sector';
import { rollMinerShipLoot } from '../gameplay/mining/MinerLoot';
import type { MissionEventBus } from '../gameplay/missions';
import { SpaceStartPortal } from '../gameplay/portals';
import type { PortalContext } from '../gameplay/portals';

export interface MainSceneContext {
  readonly input: InputManager;
  readonly camera: THREE.Camera;
  readonly audio: AudioManager;
  readonly faction: FactionSelection;
  readonly stats: PlayerStats;
  readonly shipInventory: Inventory;
  readonly shipUpgrades: ShipUpgrades;
  /** Mission event bus — MainScene emits asteroid-loot facts here so
   *  the mission system can advance tutorial steps without coupling
   *  the scene to the mission code. */
  readonly missionBus: MissionEventBus;
  /** Vibejam portal context — if `portal === true`, MainScene spawns
   *  a return portal at the player spawn so the player can hop back
   *  to the originating game. Optional so non-portal boots stay
   *  unaffected. */
  readonly portalContext?: PortalContext | null;
}

export type MapBodyKind = 'star' | 'planet' | 'moon' | 'station';

export interface MapBody {
  readonly id: string;
  readonly name: string;
  readonly kind: MapBodyKind;
  readonly radius: number;
  readonly color: number;
  readonly getWorldPosition: (out: THREE.Vector3) => THREE.Vector3;
  /** Distance from the body's center where a hyperspace jump auto-completes
   *  — i.e. the edge of its "orbit zone". For planets this matches
   *  captureRadius (so the ship arrives just outside any ring/belt). */
  readonly arrivalRadius: number;
}

export class MainScene extends Scene {
  readonly id = 'main';

  private player: PlayerShip | null = null;
  private shipControl: ShipControlSystem | null = null;
  private combat: CombatSystem | null = null;
  private landables: readonly Landable[] = [];
  private star: Star | null = null;
  private station: Station | null = null;
  private readonly spawnPoint = PLAYER_SPAWN.clone();
  private stationPos = new THREE.Vector3();
  private npcDirector: NpcDirector | null = null;
  private hostileProjectiles: HostileProjectiles | null = null;
  private asteroidRespawner: AsteroidRespawner | null = null;
  private explosions: Explosions | null = null;
  private startPortal: SpaceStartPortal | null = null;
  /** Optional observer the GameDirector can install to react to any
   *  NPC destruction (mission tagging, tutorial pirate kill detection,
   *  etc.). Kept off the mission bus on purpose — the bus is for
   *  publisher-anonymous mission events; this hook is a direct
   *  director ↔ scene channel that doesn't leak into the mission
   *  system. */
  private npcDestroyedObserver: ((npc: NpcShip) => void) | null = null;

  constructor(private readonly context: MainSceneContext) {
    super();
  }

  override async load(): Promise<void> {
    this.setupAtmosphere();
    this.setupLighting();
    this.root.add(
      createStarfield({
        starCount: 2200,
        dustCount: 7600,
        nebulaCount: 240,
        scale: 6
      })
    );

    const built = buildSector(this);
    this.landables = built.landables;
    this.star = built.star;
    this.station = built.station;
    this.spawnPoint.copy(built.spawnPoint);
    this.stationPos.copy(built.stationPosition);
    this.spawnPlayer();
    this.setupNpcs(built);
    this.spawnPortalIfNeeded();
    await super.load();
  }

  /** Drop the Vibejam return portal at the player spawn whenever the
   *  player came in through a portal hop. The portal sits a short
   *  distance "behind" the spawn (toward +Z) so the player can see it
   *  by glancing back, but isn't blocking forward flight. */
  private spawnPortalIfNeeded(): void {
    const ctx = this.context.portalContext;
    if (!ctx?.portal || !ctx.ref) return;
    const offset = new THREE.Vector3(0, 14, 90);
    const pos = this.spawnPoint.clone().add(offset);
    this.startPortal = new SpaceStartPortal(pos);
    this.root.add(this.startPortal.object3D);
  }

  getStartPortal(): SpaceStartPortal | null {
    return this.startPortal;
  }

  /** Install an observer that fires once per NPC destruction. Used by
   *  the GameDirector for mission-target kill tracking. Replaces any
   *  previous observer; pass `null` to clear. */
  setNpcDestroyedObserver(observer: ((npc: NpcShip) => void) | null): void {
    this.npcDestroyedObserver = observer;
  }

  /** Direct accessor to the ship inventory — used by mission-tagged
   *  loot callbacks (tutorial pirate drop, etc.) so they can credit
   *  cargo without re-importing the inventory module elsewhere. */
  getShipInventoryRef(): Inventory {
    return this.context.shipInventory;
  }

  getStats(): PlayerStats {
    return this.context.stats;
  }

  getNpcDirector(): NpcDirector | null {
    return this.npcDirector;
  }

  getHostileProjectiles(): HostileProjectiles | null {
    return this.hostileProjectiles;
  }

  override getFocusTarget(): THREE.Object3D | null {
    return this.player?.object3D ?? null;
  }

  getPlayerShip(): PlayerShip | null {
    return this.player;
  }

  getLandables(): readonly Landable[] {
    return this.landables;
  }

  getStar(): Star | null {
    return this.star;
  }

  getStation(): Station | null {
    return this.station;
  }

  getStationPosition(): THREE.Vector3 {
    return this.stationPos.clone();
  }

  /** Snapshot of major bodies for the system map (no asteroids). */
  getMapBodies(): readonly MapBody[] {
    const bodies: MapBody[] = [];
    if (this.star) {
      bodies.push({
        id: 'star',
        name: 'Star',
        kind: 'star',
        radius: this.star.radius,
        color: 0xffd089,
        // Stay well clear of the corona — jumping into a star is bad news.
        arrivalRadius: this.star.radius * 2.5,
        getWorldPosition: (out: THREE.Vector3) => {
          this.star!.getWorldPosition(out);
          return out;
        }
      });
    }
    for (const landable of this.landables) {
      const isMoon = landable instanceof Moon;
      const isPlanet = landable instanceof Planet;
      // Planets carry an explicit captureRadius covering body+ring+belt;
      // moons just use a moon-radius multiple — they don't have rings here.
      const arrivalRadius = isPlanet
        ? (landable as Planet).captureRadius
        : isMoon
          ? landable.radius * 5
          : landable.radius * 3;
      bodies.push({
        id: landable.displayName,
        name: landable.displayName,
        kind: isMoon ? 'moon' : isPlanet ? 'planet' : 'planet',
        radius: landable.radius,
        color: landable.palette.base,
        arrivalRadius,
        getWorldPosition: (out: THREE.Vector3) => {
          landable.getWorldPosition(out);
          return out;
        }
      });
    }
    if (this.station) {
      const stationPos = this.stationPos.clone();
      bodies.push({
        id: 'station',
        name: 'Anchor Station',
        kind: 'station',
        radius: this.station.collisionRadius,
        color: 0xb4e3ff,
        arrivalRadius: this.station.interactionRadius + 80,
        getWorldPosition: (out: THREE.Vector3) => {
          out.copy(stationPos);
          return out;
        }
      });
    }
    return bodies;
  }

  placePlayerAtStationExit(): void {
    if (!this.player || !this.station) return;
    const exit = new THREE.Vector3();
    this.station.getExitPosition(exit);
    this.player.object3D.position.copy(exit);

    const toStation = this.stationPos.clone().sub(exit);
    const yaw = Math.atan2(-toStation.x, -toStation.z);
    this.player.object3D.rotation.set(0, yaw, 0);
    this.player.velocity.set(0, 0, 0);
  }

  getShipControl(): ShipControlSystem | null {
    return this.shipControl;
  }

  getCombatSystem(): CombatSystem | null {
    return this.combat;
  }

  getSpawnPoint(): THREE.Vector3 {
    return this.spawnPoint.clone();
  }

  placePlayerAtSpawn(): void {
    if (!this.player) return;
    this.player.object3D.position.copy(this.spawnPoint);
    this.player.object3D.rotation.set(0, Math.PI, 0);
    this.player.velocity.set(0, 0, 0);
  }

  placePlayerNear(body: Landable): void {
    if (!this.player) return;
    const bodyPos = new THREE.Vector3();
    body.getWorldPosition(bodyPos);

    const awayFromOrigin = bodyPos.clone().normalize();
    if (awayFromOrigin.lengthSq() < 1e-6) awayFromOrigin.set(0, 0, 1);
    const offset = awayFromOrigin.multiplyScalar(body.radius * 2.4);
    offset.y += body.radius * 0.3;

    const spawn = bodyPos.clone().add(offset);
    this.player.object3D.position.copy(spawn);

    const toBody = bodyPos.clone().sub(spawn);
    const yaw = Math.atan2(-toBody.x, -toBody.z);
    this.player.object3D.rotation.set(0, yaw, 0);
    this.player.velocity.set(0, 0, 0);
  }

  private setupAtmosphere(): void {
    this.root.background = new THREE.Color(theme.background);
    this.root.fog = new THREE.Fog(
      theme.fog.color,
      theme.fog.near,
      theme.fog.far
    );
  }

  private setupLighting(): void {
    const { ambient, key, rim, fill } = theme.lighting;

    const ambientLight = new THREE.AmbientLight(ambient.color, ambient.intensity);

    const keyLight = new THREE.DirectionalLight(key.color, key.intensity);
    keyLight.position.set(key.position.x, key.position.y, key.position.z);

    const rimLight = new THREE.DirectionalLight(rim.color, rim.intensity);
    rimLight.position.set(rim.position.x, rim.position.y, rim.position.z);

    const fillLight = new THREE.DirectionalLight(fill.color, fill.intensity);
    fillLight.position.set(fill.position.x, fill.position.y, fill.position.z);

    this.root.add(ambientLight, keyLight, rimLight, fillLight);
  }

  private spawnPlayer(): void {
    const ship = new PlayerShip({ selection: this.context.faction });
    ship.object3D.position.copy(this.spawnPoint);
    ship.object3D.rotation.set(0, Math.PI, 0);
    this.entities.add(ship);

    // No detached trail effects on the player — the flame comes from the
    // ship's own engine plume meshes (enhanced in PlayerShip) so it stays
    // a continuous plasma column without ribbons or particle clusters.
    const trails: EngineEffect[] = [];

    const control = new ShipControlSystem(
      ship,
      this.context.input,
      gameConfig.playerShip,
      trails,
      this.context.audio,
      this.context.stats
    );
    this.systems.register(control);
    this.systems.register(new TrailUpdateSystem(trails));
    const stats = this.context.stats;
    this.systems.register(
      new CollisionSystem(ship, this.entities, {
        // Asteroid hull damage scales with closing speed and the asteroid's
        // own size — a slow scrape into a small rock barely chips paint, but
        // ramming a big rock at cruise speed does serious damage. Tuned so
        // careful flying gets through belts unhurt.
        onPlayerHit: (impactSpeed, other) => {
          const sizeFactor = Math.max(1, other.collisionRadius / 4);
          const damage = Math.min(
            40,
            (impactSpeed - 14) * 0.32 * sizeFactor
          );
          if (damage > 0) {
            // Audio cue rides on the stats subscription in DamageFlashLayer,
            // so applying damage is enough — no manual playHit here.
            stats.damageHull(damage);
          }
        }
      })
    );

    this.player = ship;
    this.shipControl = control;
  }

  private setupNpcs(built: SectorBuildResult): void {
    if (!this.player) return;
    const ship = this.player;
    const stats = this.context.stats;

    const explosions = new Explosions(this.root);
    this.explosions = explosions;
    this.systems.register(explosions);

    const hostileProjectiles = new HostileProjectiles(
      this.root,
      ship,
      stats,
      this.context.audio
    );
    this.hostileProjectiles = hostileProjectiles;
    this.systems.register(hostileProjectiles);

    const npcDirector = new NpcDirector({
      entities: this.entities,
      root: this.root,
      camera: this.context.camera,
      stationPos: this.stationPos,
      landables: this.landables,
      getPlayerShip: () => this.player,
      hostileProjectiles,
      explosions
    });
    this.npcDirector = npcDirector;
    this.systems.register(npcDirector);

    // Single source of truth for asteroid loot — both the kinetic gun and
    // the new mining beam route their kills through here so no callsite
    // re-implements the drop table. `source` lets mission code distinguish
    // gun-kills from beam-kills without coupling to either weapon.
    const dropAsteroidLoot = (
      asteroid: Asteroid,
      source: 'gun' | 'beam'
    ): void => {
      let totalAdded = 0;
      for (const drop of rollAsteroidLoot(asteroid)) {
        const accepted = this.context.shipInventory.addItem(
          drop.itemId,
          drop.count
        );
        totalAdded += accepted;
      }
      if (totalAdded > 0) {
        this.context.missionBus.emit('asteroidLootCollected', {
          count: totalAdded,
          source
        });
      }
    };

    const combat = new CombatSystem(
      ship,
      this.context.input,
      this.entities,
      this.root,
      this.context.camera,
      explosions,
      undefined,
      this.context.audio,
      {
        onAsteroidDestroyed: (asteroid: Asteroid) => {
          dropAsteroidLoot(asteroid, 'gun');
        },
        onNpcHit: (npc: NpcShip) => {
          npcDirector.onNpcAttackedByPlayer(npc);
        },
        onNpcDestroyed: (npc: NpcShip) => {
          npcDirector.onNpcAttackedByPlayer(npc);
          // Miner ships drop random mining loot; combat ships don't.
          // Loot hits the same inventory feedback path as asteroid loot.
          if (npc.role === 'miner') {
            for (const drop of rollMinerShipLoot()) {
              this.context.shipInventory.addItem(drop.itemId, drop.count);
            }
          }
          // Notify any installed observer (mission director) so it
          // can fire mission events for tagged tutorial targets
          // without the combat hook needing to know about missions.
          this.npcDestroyedObserver?.(npc);
        }
      }
    );
    this.systems.register(combat);
    this.combat = combat;

    // Right-click ship mining beam. Same drop-routing as the gun so the
    // tool used to break a rock doesn't change what falls out.
    this.systems.register(
      new ShipMiningBeam(
        ship,
        this.context.input,
        this.entities,
        this.root,
        this.context.camera,
        explosions,
        this.context.audio,
        this.context.shipUpgrades,
        undefined,
        {
          onAsteroidDestroyed: (asteroid: Asteroid) => {
            dropAsteroidLoot(asteroid, 'beam');
          }
        }
      )
    );

    const respawner = new AsteroidRespawner({
      entities: this.entities,
      getPlayerShip: () => this.player,
      globalTargetCount: built.globalAsteroidCount,
      pools: built.asteroidPools
    });
    this.asteroidRespawner = respawner;
    this.systems.register(respawner);

    this.systems.register(new PlanetaryOrbitCapture(this.entities));
    this.systems.register(new AsteroidLodSystem(this.context.camera));
  }

  getExplosions(): Explosions | null {
    return this.explosions;
  }
}
